import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, X, MapPin, ClipboardCheck } from "lucide-react";
import type { CleaningAssignment, ServiceFormTemplate, ServiceFormSubmission } from "@shared/schema";

// Member Cleaning System (Build 2) — the proof-of-completion lightbox.
// Strictly read-only: it renders an already-persisted submission (photos +
// completed checklist + location verdict). No mutations live behind it, so
// closing it mid-anything drops no work — which is also why it lives in its
// own file, away from the cleaning page's data-bearing dialogs.

export type FormsReceivedItem = {
  assignment: CleaningAssignment;
  submission: ServiceFormSubmission | null;
  template: ServiceFormTemplate | null;
  memberName: string | null;
  propertyLabel: string | null;
};

// Legacy base64 photo URLs are public-token-scoped; rewrite to the
// host-authenticated image route (same rule as the forms admin page).
function toHostImageUrl(url: string): string {
  const match = url.match(/\/api\/public\/service-form\/[^/]+\/image\/(\d+)$/);
  if (match) return `/api/app/service-forms/image/${match[1]}`;
  return url;
}

export function CleaningProofLightbox({ item, open, onOpenChange }: {
  item: FormsReceivedItem | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  if (!item) return null;
  const sub = item.submission;
  const sections = ((item.template?.sections ?? []) as Array<{
    id: string;
    title: string;
    checklistItems: Array<{ id: string; label: string }>;
    photoSlots: Array<{ id: string; label: string }>;
  }>);
  const responses = (sub?.checklistResponses ?? {}) as Record<string, boolean>;
  const photoMap = (sub?.photoUrls ?? {}) as Record<string, string[]>;
  const scheduledDate = String(item.assignment.scheduledDate).slice(0, 10);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="lightbox-cleaning-proof">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-brand" />
            {item.template?.name ?? "Cleaning proof"} — {scheduledDate}
          </DialogTitle>
        </DialogHeader>
        {!sub ? (
          <p className="text-sm text-gray-400 py-4">Submission not found.</p>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-1 text-xs text-gray-600">
              <span>
                Submitted {sub.submittedAt ? new Date(sub.submittedAt).toLocaleString() : "—"}
                {item.memberName ? ` by ${item.memberName}` : ""}
              </span>
              {sub.locationFlagged ? (
                <Badge className="bg-amber-100 text-amber-800">
                  <MapPin className="h-3 w-3 mr-1" />
                  {sub.locationDistanceMiles != null ? `${sub.locationDistanceMiles.toFixed(1)} mi away` : "Location flagged"}
                </Badge>
              ) : sub.latitude != null ? (
                <Badge className="bg-green-100 text-green-800">
                  <MapPin className="h-3 w-3 mr-1" />
                  On-site
                </Badge>
              ) : null}
            </div>
            {sections.map((section) => {
              const items = section.checklistItems ?? [];
              const slotPhotos = (section.photoSlots ?? []).flatMap((slot) => (photoMap[slot.id] ?? []).map((url) => ({ url: toHostImageUrl(url), label: slot.label })));
              if (items.length === 0 && slotPhotos.length === 0) return null;
              return (
                <div key={section.id} className="border border-gray-100 rounded-md p-2">
                  <p className="text-xs font-medium text-gray-700 mb-1">{section.title}</p>
                  {items.length > 0 && (
                    <ul className="space-y-0.5 mb-1">
                      {items.map((it) => (
                        <li key={it.id} className="flex items-center gap-1.5 text-xs">
                          {responses[it.id] ? (
                            <CheckCircle2 className="h-3 w-3 text-green-600 shrink-0" />
                          ) : (
                            <X className="h-3 w-3 text-red-400 shrink-0" />
                          )}
                          <span className={responses[it.id] ? "text-gray-600" : "text-gray-400"}>{it.label}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {slotPhotos.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {slotPhotos.map((p, i) => (
                        <a key={i} href={p.url} target="_blank" rel="noopener noreferrer" title={p.label}>
                          <img src={p.url} alt={p.label} className="w-24 h-24 object-cover rounded border" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {sub.notes && <p className="text-xs text-gray-600 whitespace-pre-wrap border-t pt-2">{sub.notes}</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
